import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Chrome, MailCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

export default function ForgotPassword() {
  const { t, ts } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-page" className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-16 spotlight">
      <SEO title={`${ts("pwd.forgot_title")} | Mr Trophée`} description={ts("pwd.forgot_sub")} path="/mot-de-passe-oublie" noindex />
      <FadeUp className="w-full max-w-md">
        <div className="bg-surface border border-gold/15 rounded-2xl p-10">
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-3">{t("login.kicker")}</p>
          <h1 className="font-serif text-4xl mb-2">{t("pwd.forgot_title")}</h1>
          {sent ? (
            <div className="text-center pt-4">
              <MailCheck className="w-12 h-12 text-gold mx-auto mb-5" />
              <p className="text-sm text-ivory/70 leading-relaxed mb-8" data-testid="forgot-done">{t("pwd.forgot_done")}</p>
              <Link to="/connexion" data-testid="forgot-back-login"
                className="inline-flex border border-gold/40 text-gold px-6 py-3 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                {t("pwd.back_login")}
              </Link>
            </div>
          ) : (
            <>
              <p className="text-ivory/70 text-sm mb-8">{t("pwd.forgot_sub")}</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">{t("login.email")}</label>
                  <input
                    type="email" name="email" autoComplete="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                  />
                </div>
                {error && <p data-testid="forgot-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 rounded-lg px-4 py-3">{error}</p>}
                <button
                  type="submit" data-testid="forgot-submit" disabled={loading}
                  className="w-full bg-gold text-ink font-semibold py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t("pwd.forgot_send")}
                </button>
              </form>
              <p className="text-center text-sm text-ivory/70 mt-6">
                <Link to="/connexion" className="text-gold hover:underline">{t("pwd.back_login")}</Link>
              </p>
            </>
          )}
        </div>
      </FadeUp>
    </div>
  );
}
